import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, ExternalLink, X, MoveRight } from "lucide-react";
import { fileSrc } from "@/lib/api";
import { LogoMark } from "@/components/site/Logo";
import { RetryImg } from "@/components/site/RetryImg";
import { WinEffects } from "./WinEffects";
import { PreparingStep, PackChooser } from "./PackChooser";

const ease = [0.22, 1, 0.36, 1];

const PackBody = () => (
  <img src="/img/gacha/pack.webp" alt="BagiPulsa Digital Booster Pack" draggable="false" className="pack-shine absolute inset-0 h-full w-full select-none" />
);

const CardFace = ({ result }) => (
  <div className={`absolute inset-0 flex flex-col overflow-hidden rounded-2xl p-3 [backface-visibility:hidden] ${result.zonk ? "bg-zinc-800" : result.rare ? "card-holo card-rare" : "card-holo"}`}>
    <div className="relative aspect-square overflow-hidden rounded-xl bg-ink">
      {result.image_url ? (
        <RetryImg src={fileSrc(result.image_url)} alt={result.name} className="h-full w-full object-cover" />
      ) : (
        <div className="grid h-full place-items-center"><LogoMark className="h-16 w-16 opacity-60" /></div>
      )}
    </div>
    <p className={`mt-3 text-center font-mono text-[11px] uppercase tracking-[0.2em] ${result.zonk ? "text-zinc-400" : "text-ink/70"}`}>{result.zonk ? "Belum beruntung" : "Selamat!"}</p>
    <p data-testid="gacha-result-name" className={`mt-1 text-center font-display text-lg font-bold leading-tight ${result.zonk ? "text-paper" : "text-ink"}`}>{result.name}</p>
  </div>
);

const CardBack = () => (
  <div className="card-back absolute inset-0 grid place-items-center rounded-2xl [backface-visibility:hidden] [transform:rotateY(180deg)]">
    <div className="absolute inset-[10px] rounded-xl border border-volt/60 shadow-[inset_0_0_24px_rgba(255,194,26,0.35)]" />
    <LogoMark className="relative h-24 w-24 drop-shadow-[0_0_22px_rgba(255,194,26,0.95)]" />
  </div>
);

const Claim = ({ result }) => {
  if (result.zonk) return <p className="text-center text-zinc-300">Coba lagi dengan kode berikutnya, semoga beruntung!</p>;
  if (result.kind === "link")
    return (
      <a data-testid="gacha-claim-link" href={result.voucher} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-volt px-6 py-3.5 font-semibold text-ink">
        Klik di sini untuk klaim voucher <ExternalLink className="h-4 w-4" />
      </a>
    );
  return (
    <div className="space-y-2 text-center">
      <p className="text-sm text-zinc-300">Kode voucher kamu:</p>
      <div className="flex items-center justify-center gap-2">
        <code data-testid="gacha-claim-code" className="rounded-xl border border-volt/40 bg-ink px-4 py-3 font-mono text-lg text-volt">{result.voucher}</code>
        <button data-testid="gacha-copy-code" onClick={() => navigator.clipboard?.writeText(result.voucher).then(() => toast.success("Kode disalin"))} className="grid h-12 w-12 place-items-center rounded-xl border border-white/15 text-paper" aria-label="Salin"><Copy className="h-4 w-4" /></button>
      </div>
      <p className="text-xs text-zinc-400">Simpan kode ini. Bisa langsung dipakai, tidak perlu konfirmasi CS.</p>
    </div>
  );
};

export const PackOpening = ({ result, title, onClose }) => {
  const [stage, setStage] = useState("prepare");
  const [progress, setProgress] = useState(0);
  const drag = useRef(null);
  const box = useRef(null);
  const toChoose = useCallback(() => setStage("choose"), []);
  const pick = useCallback(() => setStage("pack"), []);
  useEffect(() => {
    window.__lenis?.stop();
    return () => {
      window.__lenis?.start();
    };
  }, []);

  const tear = () => {
    setProgress(1);
    setStage("open");
    setTimeout(() => setStage("reveal"), 1500);
  };
  const onDown = (e) => {
    if (stage !== "pack") return;
    drag.current = box.current.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = Math.min(1, Math.max(0, (e.clientX - drag.current.left) / drag.current.width));
    setProgress((prev) => Math.max(prev, p));
    if (p > 0.92) {
      drag.current = null;
      tear();
    }
  };
  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    if (progress > 0.7) tear();
  };

  const opened = stage !== "pack";
  const pre = stage === "prepare" || stage === "choose";
  return (
    <div data-testid="gacha-pack-modal" className="fixed inset-0 z-[70] flex flex-col items-center justify-center overflow-hidden bg-ink/95 px-5 backdrop-blur-xl">
      <button data-testid="gacha-modal-close" onClick={onClose} className="absolute right-5 top-5 z-[80] grid h-11 w-11 place-items-center rounded-full border border-white/15 text-paper" aria-label="Tutup"><X className="h-5 w-5" /></button>
      {result.test ? <p data-testid="gacha-test-badge" className="absolute left-5 top-7 z-[80] rounded-full bg-lime px-3 py-1 font-mono text-[11px] font-bold uppercase text-ink">Mode uji coba</p> : null}
      <div className="pointer-events-none absolute h-[520px] w-[520px] rounded-full bg-volt/20 blur-[120px]" />
      {stage === "prepare" ? <PreparingStep onDone={toChoose} /> : null}
      {stage === "choose" ? <PackChooser title={title} onPick={pick} /> : null}
      {!pre ? (
      <motion.div initial={{ opacity: 0, scale: 0.85, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="flex w-full flex-col items-center">
      <WinEffects active={stage === "reveal" && !result.zonk} rare={!!result.rare} />
      {stage === "reveal" && result.rare ? (
        <motion.p data-testid="gacha-rare-badge" initial={{ opacity: 0, scale: 2.2, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: -4 }} transition={{ duration: 0.6, ease, delay: 0.3 }} className="rare-badge relative z-50 mb-2 rounded-full px-5 py-1.5 font-display text-sm font-bold uppercase tracking-[0.2em] text-ink">
          Kartu langka!
        </motion.p>
      ) : null}
      <div ref={box} className="relative h-[378px] w-[240px] [perspective:1200px]">
        <motion.div
          className="absolute left-[8%] top-[14%] z-10 h-[290px] w-[84%] [transform-style:preserve-3d]"
          style={{ visibility: stage === "pack" ? "hidden" : "visible" }}
          initial={{ y: 0, rotateY: 180 }}
          animate={stage === "pack" ? { y: 0, rotateY: 180 } : stage === "open" ? { y: -190, rotateY: 180 } : { y: -40, rotateY: 0, scale: 1.08 }}
          transition={{ duration: stage === "reveal" ? 0.9 : 0.9, ease, delay: stage === "open" ? 0.35 : 0 }}
        >
          <CardFace result={result} />
          <CardBack />
        </motion.div>
        <motion.div
          className="absolute inset-x-0 bottom-0 top-[10%] z-20 overflow-hidden"
          animate={stage === "reveal" ? { y: 520, opacity: 0 } : { y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease }}
        >
          <div className="absolute inset-x-0 -top-[11.111%] bottom-0"><PackBody /></div>
        </motion.div>
        <motion.div
          className="absolute inset-x-0 top-0 z-30 h-[10%] origin-bottom-right overflow-hidden"
          animate={opened ? { x: 160, y: -260, rotate: 38, opacity: 0 } : { rotate: progress * -7, y: progress * -6 }}
          transition={opened ? { duration: 0.7, ease } : { duration: 0.1 }}
        >
          <div className="absolute inset-x-0 top-0 h-[1000%]"><PackBody /></div>
        </motion.div>
        {!opened ? (
          <div
            data-testid="gacha-tear-strip"
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            className="absolute inset-x-[-20px] top-[4%] z-40 h-[12%] cursor-grab touch-none active:cursor-grabbing"
          >
            <div className="absolute left-[20px] right-[20px] top-1/2 border-t-2 border-dashed border-ink/50" />
            <div className="tear-line absolute left-[20px] top-1/2 h-[6px] -translate-y-1/2" style={{ width: `calc((100% - 40px) * ${progress})` }} />
          </div>
        ) : null}
      </div>
      <div className="relative mt-6 min-h-[120px] w-full max-w-sm">
        {stage === "pack" ? (
          <div className="text-center">
            <p className="flex items-center justify-center gap-2 font-display text-lg text-paper">Geser garis atas dari kiri ke kanan <MoveRight className="h-5 w-5 animate-pulse text-volt" /></p>
            <button data-testid="gacha-auto-tear" onClick={tear} className="mt-3 text-sm text-zinc-400 underline-offset-4 hover:text-paper hover:underline">atau ketuk di sini untuk merobek</button>
          </div>
        ) : null}
        {stage === "reveal" ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6, ease }} className="space-y-4" data-testid="gacha-claim">
            <Claim result={result} />
            {result.test ? <p data-testid="gacha-test-note" className="text-center text-xs text-lime">Mode uji coba: voucher diambil dari stok asli tapi tidak dianggap terpakai.</p> : null}
            <button data-testid="gacha-done" onClick={onClose} className="mx-auto block text-sm text-zinc-400 hover:text-paper">Selesai</button>
          </motion.div>
        ) : null}
      </div>
      </motion.div>
      ) : null}
    </div>
  );
};
